import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { addRedirectWhitelistUrl, removeRedirectWhitelistUrl } from './redirectWhitelist';
import {
  PLATFORM_SITES_FORM_FIELD_HELPER,
  PLATFORM_SITES_FORM_LABEL,
  PlatformSiteMarkersDetailGuide,
} from './platformSiteMarkersCopy';

const Form = styled.form`
  ${tw`flex flex-col gap-5`}
`;

const FieldGroup = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const Label = styled.label`
  ${tw`text-sm font-semibold text-gray-800`}
`;

const Helper = styled.p`
  ${tw`text-xs text-gray-500`}
`;

const Input = styled.input`
  ${tw`w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800`}
  &:focus {
    ${tw`outline-none ring-2 ring-green-500/30 border-green-500`}
  }
`;

const UrlList = styled.ul`
  ${tw`flex flex-col gap-2`}
`;

const UrlRow = styled.li`
  ${tw`flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800`}
`;

const IconButton = styled.button`
  ${tw`ml-auto shrink-0 rounded p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50`}
`;

const AddRow = styled.div`
  ${tw`flex gap-2`}
`;

const SecondaryButton = styled.button`
  ${tw`shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50`}
`;

const Actions = styled.div`
  ${tw`flex gap-3 pt-2`}
`;

const PrimaryButton = styled.button`
  ${tw`flex-1 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50`}
`;

const CancelButton = styled.button`
  ${tw`flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50`}
`;

export interface PlatformSiteMarkersFormValues {
  hosts: string[];
}

interface PlatformSiteMarkersFormProps {
  initialValues: PlatformSiteMarkersFormValues;
  saving?: boolean;
  onCancel: () => void;
  onSubmit: (values: PlatformSiteMarkersFormValues) => void | Promise<void>;
}

export default function PlatformSiteMarkersForm({
  initialValues,
  saving = false,
  onCancel,
  onSubmit,
}: PlatformSiteMarkersFormProps) {
  const [hosts, setHosts] = useState<string[]>(initialValues.hosts);
  const [newHost, setNewHost] = useState('');

  const handleAddHost = () => {
    const result = addRedirectWhitelistUrl(hosts, newHost);
    if (result.added) {
      setHosts(result.list);
    }
    setNewHost('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({ hosts });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <PlatformSiteMarkersDetailGuide />

      <FieldGroup>
        <Label>{PLATFORM_SITES_FORM_LABEL}</Label>
        <Helper>{PLATFORM_SITES_FORM_FIELD_HELPER}</Helper>
        {hosts.length > 0 ? (
          <UrlList>
            {hosts.map(host => (
              <UrlRow key={host}>
                <span className="break-all">{host}</span>
                <IconButton
                  type="button"
                  aria-label={`Remove ${host}`}
                  onClick={() => setHosts(prev => removeRedirectWhitelistUrl(prev, host))}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </UrlRow>
            ))}
          </UrlList>
        ) : (
          <Helper>No websites listed yet.</Helper>
        )}
        <AddRow>
          <Input
            type="text"
            placeholder="autopilotee.com"
            value={newHost}
            onChange={event => setNewHost(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddHost();
              }
            }}
          />
          <SecondaryButton type="button" onClick={handleAddHost}>
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Add
          </SecondaryButton>
        </AddRow>
      </FieldGroup>

      <Actions>
        <CancelButton type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </CancelButton>
        <PrimaryButton type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
}
